import { useEffect, useState } from 'react';

import { useDispatch, useSelector } from 'react-redux';

import { DragDropContext, Draggable, Droppable } from '@hello-pangea/dnd';
import Pagination from 'common-ui/table/Pagination';
import {
  createWorkflowStatus,
  deleteWorkflowStatus,
  getWorkflowStatuses,
  updateWorkflowStatus,
} from 'features/newsales/newsalesActions';
import {
  selectWorkflowStatuses,
  selectWorkflowStatusesCurrentPage,
  selectWorkflowStatusesDeleteSuccess,
  selectWorkflowStatusesLoading,
  selectWorkflowStatusesPagesCount,
  selectWorkflowStatusesSearchData,
  selectWorkflowStatusesSuccess,
  setWorkflowStatusesCreateSuccess,
  setWorkflowStatusesCurrentPage,
  setWorkflowStatusesDeleteSuccess,
  setWorkflowStatusesSearchData,
} from 'features/newsales/newsalesSlice';

import SearchFilter from '../components/SearchFilter';
import WorkflowStatusCard from '../components/WorkflowStatusCard';
import WorkflowStatusModal from '../components/WorkflowStatusModal';
import {
  EmptyState,
  LoadingSpinner,
  PaginationWrapper,
  StatusList,
  WorkflowStatusesContainer,
  WorkflowStatusesContent,
  WorkflowStatusesTitle,
} from './WorkflowStatuses.styles';

const WorkflowStatuses = () => {
  const dispatch = useDispatch();

  const workflowStatuses = useSelector(selectWorkflowStatuses);
  const loading = useSelector(selectWorkflowStatusesLoading);
  const currentPage = useSelector(selectWorkflowStatusesCurrentPage);
  const pagesCount = useSelector(selectWorkflowStatusesPagesCount);
  const searchData = useSelector(selectWorkflowStatusesSearchData);
  const createSuccess = useSelector(selectWorkflowStatusesSuccess);
  const deleteSuccess = useSelector(selectWorkflowStatusesDeleteSuccess);

  const [isModalOpen, setIsModalOpen] = useState(false);
  const [modalMode, setModalMode] = useState('create');
  const [selectedStatus, setSelectedStatus] = useState(null);
  const [orderedStatuses, setOrderedStatuses] = useState([]);

  const handleSearchChange = (value) => {
    dispatch(setWorkflowStatusesSearchData({ search: value }));
    dispatch(setWorkflowStatusesCurrentPage(1));
  };

  const handleStatusChange = (value) => {
    dispatch(setWorkflowStatusesSearchData({ status: value }));
    dispatch(setWorkflowStatusesCurrentPage(1));
  };

  const handlePageChange = (page) => {
    dispatch(setWorkflowStatusesCurrentPage(page));
  };

  const handleRowCountChange = (count) => {
    dispatch(setWorkflowStatusesSearchData({ limit: count }));
    dispatch(setWorkflowStatusesCurrentPage(1));
  };

  const handleOpenCreateModal = () => {
    setModalMode('create');
    setSelectedStatus(null);
    setIsModalOpen(true);
  };

  const handleOpenEditModal = (id) => {
    const statusToEdit = workflowStatuses?.find((s) => s.id === id);
    if (statusToEdit) {
      setModalMode('edit');
      setSelectedStatus(statusToEdit);
      setIsModalOpen(true);
    }
  };

  const handleCloseModal = () => {
    setIsModalOpen(false);
    setSelectedStatus(null);
  };

  const handleSubmit = (idOrData, statusData) => {
    if (modalMode === 'create') {
      return dispatch(createWorkflowStatus(idOrData))
        .unwrap()
        .then(() => {
          setIsModalOpen(false);
          dispatch(setWorkflowStatusesCreateSuccess(false));
        });
    } else {
      return dispatch(updateWorkflowStatus({ id: idOrData, data: statusData }))
        .unwrap()
        .then(() => {
          setIsModalOpen(false);
        });
    }
  };

  const handleDeleteStatus = (status) => {
    if (status.isDefault) return;

    dispatch(deleteWorkflowStatus(status.id));
  };

  const handleDragEnd = (result) => {
    if (!result.destination) return;

    const items = Array.from(orderedStatuses);
    const [reorderedItem] = items.splice(result.source.index, 1);
    items.splice(result.destination.index, 0, reorderedItem);

    setOrderedStatuses(items);

    const newDisplayOrder = result.destination.index;

    dispatch(
      updateWorkflowStatus({
        id: reorderedItem.id,
        data: {
          name: reorderedItem.name,
          description: reorderedItem.description,
          color: reorderedItem.color,
          outcomeType: reorderedItem.isDefault ? reorderedItem.outcomeType || 'none' : 'none',
          enabled: reorderedItem.enabled,
          displayOrder: newDisplayOrder,
        },
      })
    )
      .unwrap()
      .then(() => {
        const params = {
          page: currentPage,
          limit: searchData.limit,
          name: searchData.search,
        };
        if (searchData.status && searchData.status !== 'all') {
          params.status = searchData.status;
        }
        dispatch(getWorkflowStatuses(params));
      })
      .catch((error) => {
        console.error('Failed to update workflow status order:', error);
        setOrderedStatuses(orderedStatuses);
      });
  };

  useEffect(() => {
    if (workflowStatuses) {
      const sorted = [...workflowStatuses].sort((a, b) => {
        const orderA = a.displayOrder !== undefined ? a.displayOrder : 999;
        const orderB = b.displayOrder !== undefined ? b.displayOrder : 999;
        return orderA - orderB;
      });
      setOrderedStatuses(sorted);
    }
  }, [workflowStatuses]);

  useEffect(() => {
    if (deleteSuccess) {
      dispatch(setWorkflowStatusesDeleteSuccess(false));

      // If deleting the last item on a page (and not on page 1), go to previous page
      const shouldGoToPreviousPage = orderedStatuses.length === 1 && currentPage > 1;

      if (shouldGoToPreviousPage) {
        dispatch(setWorkflowStatusesCurrentPage(currentPage - 1));
      } else {
        // Refetch current page
        const params = {
          page: currentPage,
          limit: searchData.limit,
          name: searchData.search,
        };
        if (searchData.status && searchData.status !== 'all') {
          params.status = searchData.status;
        }
        dispatch(getWorkflowStatuses(params));
      }
    }
  }, [deleteSuccess, dispatch, orderedStatuses.length, currentPage, searchData]);

  useEffect(() => {
    const params = {
      page: currentPage,
      limit: searchData.limit,
      name: searchData.search,
    };
    if (searchData.status && searchData.status !== 'all') {
      params.status = searchData.status;
    }

    dispatch(getWorkflowStatuses(params));
  }, [createSuccess, dispatch, currentPage, JSON.stringify(searchData)]);

  return (
    <WorkflowStatusesContainer>
      <WorkflowStatusesTitle>Workflow Statuses</WorkflowStatusesTitle>
      <SearchFilter
        onSearchChange={handleSearchChange}
        onStatusChange={handleStatusChange}
        searchPlaceholder="Search workflow statuses..."
        showCreateButton
        onCreateClick={handleOpenCreateModal}
        createButtonText="+ Create Workflow Status"
        activeStatus="all"
      />
      <WorkflowStatusesContent>
        {loading ? (
          <LoadingSpinner>Loading...</LoadingSpinner>
        ) : !orderedStatuses || orderedStatuses.length === 0 ? (
          <EmptyState>No workflow statuses found</EmptyState>
        ) : (
          <>
            <DragDropContext onDragEnd={handleDragEnd}>
              <Droppable droppableId="workflow-statuses">
                {(provided) => (
                  <StatusList {...provided.droppableProps} ref={provided.innerRef}>
                    {orderedStatuses.map((status, index) => (
                      <Draggable key={status.id} draggableId={status.id} index={index}>
                        {(provided, snapshot) => (
                          <div
                            ref={provided.innerRef}
                            {...provided.draggableProps}
                            {...provided.dragHandleProps}
                            style={{
                              ...provided.draggableProps.style,
                              opacity: snapshot.isDragging ? 0.5 : 1,
                              cursor: snapshot.isDragging ? 'grabbing' : 'grab',
                            }}
                          >
                            <WorkflowStatusCard
                              name={status.name}
                              description={status.description}
                              color={status.color}
                              enabled={status.enabled}
                              statusType={status.statusType}
                              isDefault={status.isDefault}
                              onEdit={() => handleOpenEditModal(status.id)}
                              onDelete={() => handleDeleteStatus(status)}
                            />
                          </div>
                        )}
                      </Draggable>
                    ))}
                    {provided.placeholder}
                  </StatusList>
                )}
              </Droppable>
            </DragDropContext>

            {pagesCount > 1 && (
              <PaginationWrapper>
                <Pagination
                  currentPage={currentPage}
                  totalPages={pagesCount}
                  onPageChange={handlePageChange}
                  handleRowCountChange={handleRowCountChange}
                  dataCount={orderedStatuses.length}
                  count={searchData.limit}
                  editableRowCount
                />
              </PaginationWrapper>
            )}
          </>
        )}
      </WorkflowStatusesContent>

      <WorkflowStatusModal
        mode={modalMode}
        isOpen={isModalOpen}
        onClose={handleCloseModal}
        onCreate={handleSubmit}
        onUpdate={handleSubmit}
        initialData={selectedStatus}
      />
    </WorkflowStatusesContainer>
  );
};

export default WorkflowStatuses;
